import { db } from '../lib/firebase';
import { 
  doc, 
  deleteDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  writeBatch,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { ref, deleteObject, getMetadata } from 'firebase/storage';
import { storage } from '../lib/firebase';

export interface ContentDeletionResult {
  success: boolean;
  message: string;
  deletedItems: {
    content: boolean;
    comments: number;
    likes: number;
    watchlist: number;
    analytics: number;
    files: number;
  };
}

/**
 * Delete content and all related data
 * This includes:
 * - Content document
 * - All comments
 * - All likes/dislikes
 * - All watchlist entries
 * - All analytics data
 * - All associated files from storage
 */
export async function deleteContent(
  contentId: string, 
  userId: string
): Promise<ContentDeletionResult> {
  try {
    // Verify user owns the content
    const contentRef = doc(db, 'content', contentId);
    const contentSnap = await getDoc(contentRef);
    
    if (!contentSnap.exists()) {
      return {
        success: false,
        message: 'Content not found',
        deletedItems: {
          content: false,
          comments: 0,
          likes: 0,
          watchlist: 0,
          analytics: 0,
          files: 0
        }
      };
    }

    const contentData = contentSnap.data();
    if (contentData.uploaderId !== userId) {
      return {
        success: false,
        message: 'You do not have permission to delete this content',
        deletedItems: {
          content: false,
          comments: 0,
          likes: 0,
          watchlist: 0,
          analytics: 0,
          files: 0
        }
      };
    }

    const batch = writeBatch(db);
    let deletedComments = 0;
    let deletedLikes = 0;
    let deletedWatchlist = 0;
    let deletedAnalytics = 0;
    let deletedFiles = 0;

    // 1. Delete all comments for this content
    const commentsQuery = query(
      collection(db, 'comments'),
      where('contentId', '==', contentId)
    );
    const commentsSnap = await getDocs(commentsQuery);
    commentsSnap.docs.forEach(commentDoc => {
      batch.delete(commentDoc.ref);
      deletedComments++;
    });

    // 2. Delete all likes/dislikes for this content
    const likesQuery = query(
      collection(db, 'likes'),
      where('contentId', '==', contentId)
    );
    const likesSnap = await getDocs(likesQuery);
    likesSnap.docs.forEach(likeDoc => {
      batch.delete(likeDoc.ref);
      deletedLikes++;
    });

    // 3. Delete all watchlist entries for this content
    const watchlistQuery = query(
      collection(db, 'watchlist'),
      where('contentId', '==', contentId)
    );
    const watchlistSnap = await getDocs(watchlistQuery);
    watchlistSnap.docs.forEach(watchlistDoc => {
      batch.delete(watchlistDoc.ref);
      deletedWatchlist++;
    });

    // 4. Delete analytics data
    const analyticsCollections = ['videoStats', 'contentViewStats', 'userViewStats', 'dailyViewStats'];
    for (const collectionName of analyticsCollections) {
      const analyticsQuery = query(
        collection(db, collectionName),
        where('contentId', '==', contentId)
      );
      const analyticsSnap = await getDocs(analyticsQuery);
      analyticsSnap.docs.forEach(analyticsDoc => {
        batch.delete(analyticsDoc.ref);
        deletedAnalytics++;
      });
    }

    // 5. Delete the main content document
    batch.delete(contentRef);

    // 6. Delete associated files from storage
    const fileUrls: string[] = [];
    
    // Collect all file URLs from content data
    const collectFileUrls = (obj: any, prefix = '') => {
      if (typeof obj === 'string' && obj.includes('firebasestorage')) {
        fileUrls.push(obj);
      } else if (Array.isArray(obj)) {
        obj.forEach((item, index) => collectFileUrls(item, `${prefix}[${index}]`));
      } else if (obj && typeof obj === 'object') {
        Object.entries(obj).forEach(([key, value]) => {
          collectFileUrls(value, `${prefix}.${key}`);
        });
      }
    };

    // Collect files from all possible locations
    collectFileUrls(contentData.assets);
    collectFileUrls(contentData.thumbnail);
    collectFileUrls(contentData.poster);
    collectFileUrls(contentData.backdrop);
    collectFileUrls(contentData.coverFile);
    collectFileUrls(contentData.bookFile);
    collectFileUrls(contentData.videoFile);
    collectFileUrls(contentData.audioFile);
    collectFileUrls(contentData.subtitleFile);
    collectFileUrls(contentData.trailerFile);

    // Also check for files in episodes/seasons for series content
    if (contentData.episodes) {
      contentData.episodes.forEach((episode: any) => {
        collectFileUrls(episode.videoFile);
        collectFileUrls(episode.thumbnail);
        collectFileUrls(episode.audioFile);
        collectFileUrls(episode.subtitleFile);
      });
    }

    if (contentData.seasons) {
      contentData.seasons.forEach((season: any) => {
        collectFileUrls(season.poster);
        collectFileUrls(season.backdrop);
        if (season.episodes) {
          season.episodes.forEach((episode: any) => {
            collectFileUrls(episode.videoFile);
            collectFileUrls(episode.thumbnail);
            collectFileUrls(episode.audioFile);
            collectFileUrls(episode.subtitleFile);
          });
        }
      });
    }

    // Delete files from storage
    for (const fileUrl of fileUrls) {
      try {
        if (fileUrl && fileUrl.includes('firebasestorage')) {
          // Extract the path from the full URL
          // Firebase Storage URLs format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
          let filePath = fileUrl;
          
          // If it's a full URL, extract the path
          if (fileUrl.includes('firebasestorage.googleapis.com')) {
            try {
              const url = new URL(fileUrl);
              // Extract path from URL pathname (format: /v0/b/{bucket}/o/{encodedPath})
              const pathMatch = url.pathname.match(/\/o\/(.+)/);
              if (pathMatch) {
                // Decode the path (it's URL encoded)
                filePath = decodeURIComponent(pathMatch[1]);
              } else {
                // Fallback: try to extract from query params or use full URL
                filePath = fileUrl;
              }
            } catch (urlError) {
              // If URL parsing fails, try to extract path manually
              const pathMatch = fileUrl.match(/\/o\/([^?]+)/);
              if (pathMatch) {
                filePath = decodeURIComponent(pathMatch[1]);
              }
            }
          }
          
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
          deletedFiles++;
        }
      } catch (error) {
        console.error('Failed to delete file from storage:', fileUrl, error);
        // Continue with other files even if one fails
      }
    }

    // Execute all deletions
    await batch.commit();

    return {
      success: true,
      message: 'Content and all related data deleted successfully',
      deletedItems: {
        content: true,
        comments: deletedComments,
        likes: deletedLikes,
        watchlist: deletedWatchlist,
        analytics: deletedAnalytics,
        files: deletedFiles
      }
    };

  } catch (error) {
    console.error('Error deleting content:', error);
    return {
      success: false,
      message: 'Failed to delete content. Please try again.',
      deletedItems: {
        content: false,
        comments: 0,
        likes: 0,
        watchlist: 0,
        analytics: 0,
        files: 0
      }
    };
  }
}


/**
 * Get user's content for management
 */
export async function getUserContent(userId: string): Promise<any[]> {
  try {
    const contentQuery = query(
      collection(db, 'content'),
      where('uploaderId', '==', userId),
      where('status', '!=', 'deleted')
    );
    
    const contentSnap = await getDocs(contentQuery);
    return contentSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching user content:', error);
    return [];
  }
}

/**
 * Restore soft-deleted content
 */
export async function restoreContent(
  contentId: string, 
  userId: string
): Promise<ContentDeletionResult> {
  try {
    const contentRef = doc(db, 'content', contentId);
    
    await updateDoc(contentRef, {
      status: 'published',
      deletedAt: null,
      deletedBy: null
    });

    return {
      success: true,
      message: 'Content has been restored',
      deletedItems: {
        content: false,
        comments: 0,
        likes: 0,
        watchlist: 0,
        analytics: 0,
        files: 0
      }
    };

  } catch (error) {
    console.error('Error restoring content:', error);
    return {
      success: false,
      message: 'Failed to restore content. Please try again.',
      deletedItems: {
        content: false,
        comments: 0,
        likes: 0,
        watchlist: 0,
        analytics: 0,
        files: 0
      }
    };
  }
}
